import React, {useEffect, useState} from 'react';
import {Modal, Table, Button, AutoComplete, Checkbox} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {TestControllerApi, TestDto, TestExecutionDto, TestPlanDto, TestPlanTestDto} from "../../../api";
import {useKeycloakHeader} from "../../../config/Config";
import {formatDateTime} from "../../../utils/Utils";

interface TestPlanCreationModalProps {
    visible: boolean;
    testPlan: TestPlanDto;
    onClose: () => void;
    onUpdateTests: (tests: TestPlanTestDto[]) => void;
}

/*
Dieses Modal ist zur Erstellung eines Testplans zuständig
 */
const TestPlanCreationModal: React.FC<TestPlanCreationModalProps> = ({
                                                                         visible,
                                                                         testPlan,
                                                                         onClose,
                                                                         onUpdateTests
                                                                     }) => {
    const [newTestDtoList, setNewTestPlanTestDtos] = useState<TestPlanTestDto[]>([]);
    const [name, setName] = useState<string>('');
    const [fetchedTests, setFetchedTests] = useState<TestPlanTestDto[]>([]);
    const [searchOptions, setSearchOptions] = useState<{ value: string }[]>([]);
    const [filteredOptions, setFilteredOptions] = useState<{ value: string }[]>([]);
    const headers = useKeycloakHeader();
    const testControllerApi = new TestControllerApi();

    useEffect(() => {
        if(testPlan.testPlanTestDtos){
            setNewTestPlanTestDtos(testPlan.testPlanTestDtos);
            fetchTests();
        }
    }, [JSON.stringify(testPlan.testPlanTestDtos), visible]);

    const fetchTests = () => {
        testControllerApi.getAllTestsMinimal(headers).then((axiosResponse) => {
            const testDtos: TestDto[] = axiosResponse.data;
            const testPlanTestDtos: Array<TestPlanTestDto> = axiosResponse.data.map((testDto: TestDto) => ({
                testDto: testDto,
                activeTrace: false
            } as TestPlanTestDto));
            setFetchedTests(testPlanTestDtos);
            const options = testDtos.map((test) => ({value: test.name || ''}));
            setSearchOptions(options);
            setFilteredOptions(options);
        }).catch((error) => {
            console.error(error);
        });
    };

    const handleAddTest = (value: string) => {
        const selectedTest = fetchedTests.find(test => test.testDto?.name === value);
        if (selectedTest) {
            setNewTestPlanTestDtos([...newTestDtoList, selectedTest]);
        }
        setName('');
        setFilteredOptions(searchOptions);
    };

    // Funktion zum Löschen eines einzelnen Tests hinzufügen
    const handleRemoveButton = (test: TestPlanTestDto) => {
        if(test.testDto?.id){
            setNewTestPlanTestDtos(newTestDtoList.filter(t => t.testDto?.id != test.testDto?.id));
        }
    };

    // Funktion zum Bestätigen der Löschung
    const handleConfirm = () => {
        onUpdateTests(newTestDtoList);
    };

    const handleNameChange = (value: string) => {
        setName(value);
        const filteredOptions = searchOptions.filter(option =>
            option.value.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredOptions(filteredOptions);
    };

    const handleCheckboxChange = (test: TestPlanTestDto, checked: boolean) => {
        const updatedTests = newTestDtoList.map(t =>
            t.testDto?.id === test.testDto?.id ? {...t, activeTrace: checked} : t
        );
        setNewTestPlanTestDtos(updatedTests); // Aktualisiere den State
    };

    // Spalten der Tabelle
    const columns = [
        {
            title: 'Test Name',
            dataIndex: ['testDto','name'],
            key: 'name',
        },
        {
            title: 'Beschreibung',
            dataIndex: ['testDto','description'],
            key: 'description',
        },
        {
            title: 'System',
            dataIndex: ['testDto','system', 'name'],
            key: 'system',
        },
        {
            title: 'Aufzeichnen',
            key: 'record',
            render: (record: TestPlanTestDto)=> (
                <Checkbox
                    checked={record.activeTrace}
                    onChange={(e) => handleCheckboxChange(record, e.target.checked)}
                >
                    Aufzeichnung
                </Checkbox>
            ),
        },
        {
            title: 'Aktionen',
            key: 'actions',
            render: (_: any, record: TestPlanTestDto) => (
                <Button
                    danger
                    onClick={() => handleRemoveButton(record)}
                    style={{ marginRight: '8px', border: '0px' }}
                >
                    <DeleteOutlined />
                </Button>
            ),
        },
    ];

    return (
        <Modal
            title="Testplan Details"
            open={visible}
            onCancel={onClose}
            width={1000}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Abbrechen
                </Button>,
                <Button
                    type="primary"
                    onClick={handleConfirm}
                >
                    Bestätigen
                </Button>,
            ]}
        >
            <AutoComplete
                options={filteredOptions}
                placeholder="Suche nach Test"
                onSelect={handleAddTest}
                style={{marginBottom: 16, width: '100%'}}
                onChange={handleNameChange}
                value={name}
            />
            <Table
                dataSource={newTestDtoList}
                columns={columns}
                rowKey="id"
                locale={{emptyText: 'Keine Daten vorhanden'}}
                pagination={false}
            />
        </Modal>
    );
};

export default TestPlanCreationModal;
